import openai from './openaiClient';

/**
 * Analyzes post content and determines if it matches keyword criteria
 * @param {string} postContent - Content of the post to analyze
 * @param {string[]} targetKeywords - Keywords to match against
 * @param {string} matchType - 'any' or 'all' keywords must match
 * @returns {Promise<Object>} Match analysis result
 */
export async function analyzePostForKeywords(postContent, targetKeywords, matchType = 'any') {
  try {
    const keywordsList = targetKeywords?.join(', ');
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-nano',
      messages: [
        { 
          role: 'system', 
          content: 'You are a content analyzer that determines if posts match specific topics or keywords. Be precise and consider context, not just exact word matches.' 
        },
        { 
          role: 'user', 
          content: `Analyze if this post is relevant to these keywords/topics: ${keywordsList}\n\nPost content: "${postContent}"\n\nMatch type: ${matchType === 'all' ? 'ALL keywords must be relevant' : 'ANY keyword can be relevant'}\n\nConsider synonyms, related concepts, and context.` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'keyword_analysis',
          schema: {
            type: 'object',
            properties: {
              isMatch: { 
                type: 'boolean',
                description: 'Whether the post matches the keyword criteria'
              },
              matchedKeywords: { 
                type: 'array', 
                items: { type: 'string' },
                description: 'Keywords that were found or are contextually relevant'
              },
              relevanceScore: { 
                type: 'number',
                minimum: 0,
                maximum: 1,
                description: 'How relevant the post is to the keywords (0-1)'
              },
              reasoning: { 
                type: 'string',
                description: 'Brief explanation of the analysis'
              }
            },
            required: ['isMatch', 'matchedKeywords', 'relevanceScore', 'reasoning'],
            additionalProperties: false,
          },
        },
      },
      reasoning_effort: 'minimal',
      verbosity: 'low',
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error analyzing post for keywords:', error);
    throw new Error('Failed to analyze post. Please try again.');
  }
}

/**
 * Generates an AI-powered comment for a post
 * @param {Object} options - Comment generation options
 * @param {string} options.postContent - Content of the post to comment on
 * @param {string} options.commentStyle - Style of comment (supportive, insightful, question, humorous)
 * @param {string} options.brandVoice - Brand voice to maintain
 * @param {string[]} options.keywords - Keywords that triggered this comment
 * @param {number} options.maxLength - Maximum comment length
 * @returns {Promise<string>} Generated comment text
 */
export async function generateComment(options) {
  const { 
    postContent, 
    commentStyle = 'supportive', 
    brandVoice = 'professional', 
    keywords = [], 
    maxLength = 200 
  } = options;

  try {
    const keywordContext = keywords?.length > 0 
      ? `This post was identified because it relates to: ${keywords?.join(', ')}.` 
      : '';

    const styleGuide = {
      supportive: 'encouraging and positive',
      insightful: 'thoughtful and adds value',
      question: 'curious and engaging with a question',
      humorous: 'light-hearted and fun while staying professional'
    };

    const prompt = `Generate a ${styleGuide?.[commentStyle]} comment for this social media post.

Post content: "${postContent}"

${keywordContext}

Requirements:
- Brand voice: ${brandVoice}
- Maximum length: ${maxLength} characters
- Be authentic and human-like
- Add value to the conversation
- Avoid generic responses
- DO NOT use hashtags in comments
- Sound natural, not robotic

Generate only the comment text, no explanations or quotes.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: 'You are a skilled social media community manager who writes authentic, engaging comments that build relationships and drive meaningful conversations.' 
        },
        { role: 'user', content: prompt },
      ],
      reasoning_effort: 'low',
      verbosity: 'low',
      max_completion_tokens: 300
    });

    return response?.choices?.[0]?.message?.content?.trim();
  } catch (error) {
    console.error('Error generating comment:', error);
    throw new Error('Failed to generate comment. Please try again.');
  }
}

/**
 * Generates multiple comment variations
 * @param {Object} options - Comment generation options
 * @param {number} variations - Number of variations to generate
 * @returns {Promise<Array>} Array of generated comments
 */
export async function generateCommentVariations(options, variations = 3) {
  try {
    const comments = await Promise.all(
      Array.from({ length: variations }, () => generateComment(options))
    );
    return comments;
  } catch (error) {
    console.error('Error generating comment variations:', error);
    throw new Error('Failed to generate comment variations. Please try again.');
  }
}

/**
 * Moderates comment content for safety
 * @param {string} comment - Comment text to moderate
 * @returns {Promise<Object>} Moderation results
 */
export async function moderateComment(comment) {
  try {
    const response = await openai?.moderations?.create({
      model: 'text-moderation-latest',
      input: comment,
    });

    const result = response?.results?.[0];
    
    return {
      isSafe: !result?.flagged,
      categories: result?.categories,
      categoryScores: result?.category_scores,
      flagged: result?.flagged
    };
  } catch (error) {
    console.error('Error moderating comment:', error);
    throw new Error('Failed to moderate comment. Please try again.');
  }
}

/**
 * Analyzes post sentiment and engagement potential
 * @param {string} postContent - Post content to analyze
 * @returns {Promise<Object>} Sentiment and engagement analysis
 */
export async function analyzePostSentiment(postContent) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-nano',
      messages: [
        { 
          role: 'system', 
          content: 'You are a social media analyst specializing in sentiment analysis and engagement prediction.' 
        },
        { 
          role: 'user', 
          content: `Analyze this post for sentiment and engagement potential:\n\n"${postContent}"` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'sentiment_analysis',
          schema: {
            type: 'object',
            properties: {
              sentiment: { 
                type: 'string',
                enum: ['positive', 'negative', 'neutral', 'mixed'],
                description: 'Overall sentiment of the post'
              },
              emotionalTone: { 
                type: 'array', 
                items: { type: 'string' },
                description: 'Emotional tones present in the post'
              },
              engagementPotential: { 
                type: 'string',
                enum: ['high', 'medium', 'low'],
                description: 'Predicted engagement level'
              },
              recommendedAction: { 
                type: 'string',
                enum: ['comment', 'skip', 'monitor'],
                description: 'Recommended action for this post'
              },
              reasoning: { 
                type: 'string',
                description: 'Brief explanation of the analysis'
              }
            },
            required: ['sentiment', 'emotionalTone', 'engagementPotential', 'recommendedAction', 'reasoning'],
            additionalProperties: false,
          },
        },
      },
      reasoning_effort: 'minimal',
      verbosity: 'low',
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error analyzing post sentiment:', error);
    throw new Error('Failed to analyze post sentiment. Please try again.');
  }
}